import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, ClockIcon, ArrowLeftIcon, CalendarIcon } from 'lucide-react';
const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('maintenance');
  // Sample data for obligations
  const propertyObligations = [{
    id: 1,
    name: 'Pool Permit',
    description: 'Annual permit required for all pools',
    dueDate: '2026-12-31',
    recurring: 'Annually',
    responsibleParty: 'On-Site Maintenance',
    status: 'Completed',
    attributeCondition: 'Pool equals 2'
  }, {
    id: 2,
    name: 'BBQ Inspection',
    description: 'Safety inspection for all BBQ grills',
    dueDate: '2026-10-15',
    recurring: 'Annually',
    responsibleParty: 'Tenant',
    status: 'Pending',
    attributeCondition: 'Grills does not equal 0'
  }, {
    id: 3,
    name: 'Elevator Maintenance',
    description: 'Regular maintenance check required by law',
    dueDate: '2026-09-30',
    recurring: 'Quarterly',
    responsibleParty: 'Outsourced to Vendor',
    status: 'Overdue',
    attributeCondition: 'Elevator contains Service'
  }, {
    id: 4,
    name: 'Water Tank Cleaning',
    description: 'Required for NYC properties',
    dueDate: '2026-11-15',
    recurring: 'Annually',
    responsibleParty: 'Corporate Team',
    status: 'Scheduled',
    propertyField: 'Ownership Group equals Greystar'
  }, {
    id: 5,
    name: 'Fitness Center Equipment Inspection',
    description: 'Safety check for all gym equipment',
    dueDate: '2026-12-10',
    recurring: 'Bi-annually',
    responsibleParty: 'On-Site Maintenance',
    status: 'Pending',
    attributeCondition: 'Fitness Center equals Yes'
  }];
  // Sample data for VGBA Obligations
  const vgbaObligations = [{
    id: 1,
    buildingName: 'Sunset Towers',
    dueDate: '2026-09-15',
    responsibleParty: 'On-Site Maintenance',
    status: 'Completed'
  }, {
    id: 2,
    buildingName: 'Riverside Heights',
    dueDate: '2026-10-20',
    responsibleParty: 'Corporate Team',
    status: 'Completed'
  }, {
    id: 3,
    buildingName: 'Oakwood Gardens',
    dueDate: '2026-08-05',
    responsibleParty: 'Outsourced to Vendor',
    status: 'Overdue'
  }, {
    id: 4,
    buildingName: 'Maple Ridge Apartments',
    dueDate: '2026-11-30',
    responsibleParty: 'On-Site Maintenance',
    status: 'Exempt'
  }, {
    id: 5,
    buildingName: 'Parkview Residences',
    dueDate: '2026-01-15',
    responsibleParty: 'Corporate Team',
    status: 'Scheduled'
  }, {
    id: 6,
    buildingName: 'Harbor View Condos',
    dueDate: '2026-09-25',
    responsibleParty: 'Outsourced to Vendor',
    status: 'Completed'
  }, {
    id: 7,
    buildingName: 'Willow Creek Apartments',
    dueDate: '2026-10-10',
    responsibleParty: 'On-Site Maintenance',
    status: 'Pending'
  }, {
    id: 8,
    buildingName: 'Cedar Heights',
    dueDate: '2026-09-05',
    responsibleParty: 'Corporate Team',
    status: 'Completed'
  }, {
    id: 9,
    buildingName: 'Pine Valley Residences',
    dueDate: '2026-08-20',
    responsibleParty: 'Outsourced to Vendor',
    status: 'Completed'
  }, {
    id: 10,
    buildingName: 'Meadow Brook Apartments',
    dueDate: '2026-09-10',
    responsibleParty: 'On-Site Maintenance',
    status: 'Completed'
  }];
  // Function to render status badge
  const renderStatusBadge = status => {
    switch (status) {
      case 'Completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-323">
            <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-324" />
            Compliant
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-325">
            <ClockIcon className="mr-1 h-3 w-3" data-id="element-326" />
            Expiring Soon
          </span>;
      case 'Overdue':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-327">
            <XCircleIcon className="mr-1 h-3 w-3" data-id="element-328" />
            Non-Compliant
          </span>;
      case 'Scheduled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800" data-id="element-329">
            <CalendarIcon className="mr-1 h-3 w-3" data-id="element-330" />
            Other
          </span>;
      case 'Exempt':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-331">
            Exempt
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-332">
            {status}
          </span>;
    }
  };
  // Function to format dates for display
  const formatDate = dateString => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  // Function to determine row background based on status
  const getRowBackgroundClass = status => {
    switch (status) {
      case 'Overdue':
        return 'bg-red-50';
      default:
        return 'hover:bg-gray-50';
    }
  };
  // Function to determine text color based on status
  const getTextColorClass = status => {
    switch (status) {
      case 'Overdue':
        return 'text-red-900';
      default:
        return 'text-gray-900';
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-333">
      <header className="bg-white shadow" data-id="element-334">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-335">
          <div className="flex justify-between items-center" data-id="element-336">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-337">Reports</h1>
            <div className="flex space-x-3" data-id="element-338">
              <Link to="/settings" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
              backgroundColor: '#332275'
            }} data-id="element-339">
                Settings
              </Link>
              <Link to="/property-details" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-opacity" style={{
              backgroundColor: '#332275'
            }} data-id="element-340">
                View Property Details
              </Link>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-341">
        {/* Tab Navigation */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-342">
          <div className="border-b border-gray-200" data-id="element-343">
            <nav className="flex -mb-px" data-id="element-344">
              <button onClick={() => setActiveTab('maintenance')} className={`${activeTab === 'maintenance' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors`} data-id="element-345">
                Maintenance Requirements
              </button>
              <button onClick={() => setActiveTab('vgba')} className={`${activeTab === 'vgba' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors`} data-id="element-346">
                VGBA Obligations
              </button>
            </nav>
          </div>
          {/* Tab Content */}
          {activeTab === 'maintenance' && <div data-id="element-347">
              <div className="px-6 py-6 border-b border-gray-200 bg-gray-50" data-id="element-348">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-349">
                  Castle Apartment Homes - 123 Castle Street New York, NY 10001
                </h2>
              </div>
              <div className="overflow-x-auto" data-id="element-350">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-351">
                  <thead className="bg-gray-50" data-id="element-352">
                    <tr data-id="element-353">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-354">
                        Obligation
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-355">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-356">
                        Frequency
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-357">
                        Responsible Party
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-358">
                        Due Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-359">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-360">
                    {propertyObligations.map(obligation => <tr key={obligation.id} className={getRowBackgroundClass(obligation.status)} data-id="element-361">
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${getTextColorClass(obligation.status)}`} data-id="element-362">
                          {obligation.name}
                        </td>
                        <td className={`px-6 py-4 text-sm ${obligation.status === 'Overdue' ? 'text-red-700' : 'text-gray-500'} max-w-xs`} data-id="element-363">
                          <div className="truncate" data-id="element-364">
                            {obligation.description}
                          </div>
                          <div className="text-xs text-gray-400 mt-1 italic" data-id="element-365">
                            {obligation.attributeCondition || obligation.propertyField}
                          </div>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${obligation.status === 'Overdue' ? 'text-red-700' : 'text-gray-500'}`} data-id="element-366">
                          {obligation.recurring}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${obligation.status === 'Overdue' ? 'text-red-700' : 'text-gray-500'}`} data-id="element-367">
                          {obligation.responsibleParty}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${obligation.status === 'Overdue' ? 'text-red-700' : 'text-gray-500'}`} data-id="element-368">
                          {formatDate(obligation.dueDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-369">
                          {renderStatusBadge(obligation.status)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
          {activeTab === 'vgba' && <div data-id="element-370">
              <div className="overflow-x-auto" data-id="element-371">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-372">
                  <thead className="bg-gray-50" data-id="element-373">
                    <tr data-id="element-374">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-375">
                        Property Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-376">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-377">
                        Due Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-378">
                        Responsible Party
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-379">
                    {vgbaObligations.map(obligation => <tr key={obligation.id} className={getRowBackgroundClass(obligation.status)} data-id="element-380">
                        <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${getTextColorClass(obligation.status)}`} data-id="element-381">
                          {obligation.buildingName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-382">
                          {renderStatusBadge(obligation.status)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${obligation.status === 'Overdue' ? 'text-red-700' : obligation.status === 'Pending' ? 'text-yellow-700' : 'text-gray-500'}`} data-id="element-383">
                          {formatDate(obligation.dueDate)}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${obligation.status === 'Overdue' ? 'text-red-700' : obligation.status === 'Pending' ? 'text-yellow-700' : 'text-gray-500'}`} data-id="element-384">
                          {obligation.responsibleParty}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>
      </main>
    </div>;
};
export default ReportsPage;